import { createSelector } from 'reselect';

import type { GlobalStateType } from 'Lib/reduxType';
import { createKeySelector } from 'Lib/reduxUtils';
import isNil from 'Lib/utils/isNil';
import { milliseconds as perfTimer } from 'Lib/utils/perfTimer';

export const selectSession = createKeySelector('session');

export const getLastActivity = createSelector(
  selectSession,
  (session): number | null => session.lastActivity
);

export const getLastActivityPerfTime = createSelector(
  selectSession,
  (session): number | null => session.lastActivityPerfTime
);

export const getIsExpired = createSelector(selectSession, (session): boolean => session.isExpired);

export const getAppSessionId = createSelector(
  selectSession,
  (session): string | null => session.appSessionId
);

export const getAppSessionStart = createSelector(
  selectSession,
  (session): number | null => session.appSessionStart
);

export const getAppSessionTimerStart = createSelector(
  selectSession,
  (session): number | null => session.appSessionStartPerfTime
);

export const getAppSessionTimeElapsed = createSelector(
  [getAppSessionTimerStart, (_state: GlobalStateType, now: number = perfTimer()) => now],
  (sessionStart, now): number | null => (!isNil(sessionStart) ? now - sessionStart : null)
);

export const getTimeSinceLastActivity = createSelector(
  [
    selectSession,
    getLastActivityPerfTime,
    (_state: GlobalStateType, now: number = perfTimer()) => now,
  ],
  (_, last, now): number | null => (!isNil(last) ? now - last : null)
);

export const getUserSessionId = createSelector(
  selectSession,
  (session): string | null => session.userSessionId ?? null
);

export const getUserSessionStart = createSelector(
  selectSession,
  (session): number | null => session.userSessionStart ?? null
);

const getUserSessionTimerStart = createSelector(
  selectSession,
  (session): number | null => session.userSessionStartPerfTime
);

export const getUserSessionTimeElapsed = createSelector(
  [getUserSessionTimerStart, (_state: GlobalStateType, now: number = perfTimer()) => now],
  (sessionStart, now): number | null => (!isNil(sessionStart) ? now - sessionStart : null)
);

/*
 * Attempts to calculate current time that is comparable to appSessionStart - if performance.Now() is
 * available in the browser, the result is not affected by clock resets.
 */
export const getAppSessionAdjustedTime = createSelector(
  [getAppSessionStart, getAppSessionTimeElapsed],
  (start, timeElapsed): number =>
    !isNil(start) && !isNil(timeElapsed) ? start + timeElapsed : Date.now()
);
